import { useCallback, useEffect, useState } from 'react';
import { mediaList } from '@/mocks/movies';

const STORAGE_KEY = 'boluomi-favorites';

const readFavorites = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const writeFavorites = (ids: string[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* storage unavailable, ignore */
  }
};

export function useFavorites() {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    setIds(readFavorites());

    const sync = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setIds(readFavorites());
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const toggle = useCallback((id: string) => {
    setIds((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [id, ...prev];
      writeFavorites(next);
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setIds((prev) => {
      const next = prev.filter((item) => item !== id);
      writeFavorites(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setIds([]);
    writeFavorites([]);
  }, []);

  const isFavorite = useCallback((id: string) => ids.includes(id), [ids]);

  const items = ids
    .map((id) => mediaList.find((media) => media.id === id))
    .filter((item): item is (typeof mediaList)[number] => Boolean(item));

  return { ids, items, toggle, remove, clear, isFavorite };
}